// src/presentation/components/PatientsPagination.tsx
import React from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

interface PatientsPaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}

export const PatientsPagination: React.FC<PatientsPaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
}) => {
  const { t } = useTranslation("patients");
  const safeTotalPages = Math.max(totalPages, 1);
  const safeCurrentPage = Math.min(Math.max(currentPage, 1), safeTotalPages);
  const startItem = totalItems === 0 ? 0 : (safeCurrentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(safeCurrentPage * itemsPerPage, totalItems);

  const getPageNumbers = () => {
    const pages: number[] = [];
    const maxVisible = 5;

    if (safeTotalPages <= maxVisible) {
      for (let i = 1; i <= safeTotalPages; i++) {
        pages.push(i);
      }
    } else {
      if (safeCurrentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push(-1);
        pages.push(safeTotalPages);
      } else if (safeCurrentPage >= safeTotalPages - 2) {
        pages.push(1);
        pages.push(-1);
        for (let i = safeTotalPages - 3; i <= safeTotalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push(-1);
        for (let i = safeCurrentPage - 1; i <= safeCurrentPage + 1; i++) pages.push(i);
        pages.push(-1);
        pages.push(safeTotalPages);
      }
    }

    return pages;
  };

  return (
    <div className="mt-4 flex flex-col gap-3 rounded-xl border bg-white px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-4">
      <div className="text-center text-sm text-gray-600 sm:text-left">
        {t("pagination.showing", { start: startItem, end: endItem, total: totalItems })}
      </div>

      <div className="flex items-center justify-between gap-2 sm:justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(safeCurrentPage - 1)}
          disabled={safeCurrentPage === 1}
          className="h-9 min-w-9 cursor-pointer rounded-lg bg-white hover:bg-white"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>

        <div className="flex items-center gap-1 sm:hidden">
          <span className="text-sm font-medium text-gray-700">
            {t("pagination.pageOf", { current: safeCurrentPage, total: safeTotalPages })}
          </span>
        </div>

        <div className="hidden items-center gap-2 sm:flex">
          {getPageNumbers().map((page, index) =>
            page === -1 ? (
              <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                ...
              </span>
            ) : (
              <Button
                key={page}
                variant="outline"
                size="sm"
                onClick={() => onPageChange(page)}
                aria-current={safeCurrentPage === page ? "page" : undefined}
                className={
                  safeCurrentPage === page
                    ? "h-9 min-w-9 cursor-pointer border-primary bg-white text-primary hover:bg-white hover:text-primary"
                    : "h-9 min-w-9 cursor-pointer bg-white hover:bg-white"
                }
              >
                {page}
              </Button>
            ),
          )}
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(safeCurrentPage + 1)}
          disabled={safeCurrentPage === safeTotalPages}
          className="h-9 min-w-9 cursor-pointer rounded-lg bg-white hover:bg-white"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};
